import { createSlice } from "@reduxjs/toolkit";
import {
  fetchDevicesThunk,
  toggleDeviceThunk,
  exportDevicesThunk,
  updateDevicePrefsThunk,
} from "../thunks/deviceThunks";

const initialState = {
  rows: [],
  loading: false,
  error: null,
  filters: {
    platform: "", // ANDROID, IOS, WEB (uppercase to match backend enum)
    isActive: "",
    "appInfo.version": "",
    "prefs.promoPush": "",
    search: "",
    dateFrom: "",
    dateTo: "",
  },
  pagination: {
    nextCursor: null,
    hasMore: true,
  },
};

const deviceSlice = createSlice({
  name: "notificationDevices",
  initialState,
  reducers: {
    setDeviceFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      // Reset pagination when filters change
      state.rows = [];
      state.pagination = {
        nextCursor: null,
        hasMore: true,
      };
    },
    clearDeviceError: (state) => {
      state.error = null;
    },
    setDevicePrefs: (state, action) => {
      const { deviceId, prefs } = action.payload;
      const index = state.rows.findIndex((d) => d._id === deviceId);
      if (index >= 0) {
        state.rows[index].prefs = { ...state.rows[index].prefs, ...prefs };
      }
    },
    setDeviceQuietHours: (state, action) => {
      const { deviceId, quietHours } = action.payload;
      const index = state.rows.findIndex((d) => d._id === deviceId);
      if (index >= 0) {
        state.rows[index].quietHours = quietHours;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch devices
    builder
      .addCase(fetchDevicesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDevicesThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.reset) {
          state.rows = action.payload.devices;
        } else {
          state.rows = [...state.rows, ...action.payload.devices];
        }
        state.pagination.nextCursor = action.payload.nextCursor;
        state.pagination.hasMore = action.payload.hasMore;
      })
      .addCase(fetchDevicesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Toggle device
    builder
      .addCase(toggleDeviceThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleDeviceThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Use _id instead of id for finding the device
        const index = state.rows.findIndex((d) => d._id === action.payload._id);
        if (index >= 0) {
          state.rows[index] = action.payload;
        }
      })
      .addCase(toggleDeviceThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Export devices
    builder
      .addCase(exportDevicesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportDevicesThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportDevicesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update device preferences
    builder
      .addCase(updateDevicePrefsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDevicePrefsThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { deviceId, prefs, quietHours } = action.payload;
        const index = state.rows.findIndex((d) => d._id === deviceId);
        if (index >= 0) {
          state.rows[index].prefs = prefs;
          if (quietHours) {
            state.rows[index].quietHours = quietHours;
          } else {
            state.rows[index].quietHours = null;
          }
        }
      })
      .addCase(updateDevicePrefsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setDeviceFilters, clearDeviceError } = deviceSlice.actions;

export default deviceSlice.reducer;
